/**
 * Market Session Banner
 * Shows current active trading session with countdown
 */
import { useState, useEffect } from 'react';
import { AlertTriangle, Clock, Globe, TrendingUp } from 'lucide-react';

const SESSIONS = {
  SYDNEY: { start: 22, end: 7, color: '#3B82F6', pairs: ['AUD/USD', 'NZD/USD', 'AUD/JPY'] },
  TOKYO: { start: 0, end: 9, color: '#F97316', pairs: ['USD/JPY', 'EUR/JPY', 'GBP/JPY'] },
  LONDON: { start: 8, end: 17, color: '#10B981', pairs: ['EUR/USD', 'GBP/USD', 'EUR/GBP'] },
  NEW_YORK: { start: 13, end: 22, color: '#EF4444', pairs: ['USD/CAD', 'EUR/USD', 'GBP/USD', 'XAU/USD'] }
};

const getActiveSessions = () => {
  const now = new Date();
  const utcHour = now.getUTCHours();
  
  const active = [];
  
  Object.entries(SESSIONS).forEach(([name, session]) => {
    let isActive = false;
    
    if (session.start < session.end) {
      isActive = utcHour >= session.start && utcHour < session.end;
    } else {
      // Overnight session (e.g., Sydney 22:00 - 07:00)
      isActive = utcHour >= session.start || utcHour < session.end;
    }
    
    if (isActive) {
      // Calculate time until session end
      let hoursRemaining;
      if (session.start < session.end) {
        hoursRemaining = session.end - utcHour;
      } else {
        hoursRemaining = utcHour >= session.start 
          ? (24 - utcHour) + session.end 
          : session.end - utcHour;
      }
      
      active.push({
        name,
        ...session,
        hoursRemaining
      });
    }
  });
  
  return active;
};

const isMarketClosed = () => {
  const now = new Date();
  const day = now.getUTCDay();
  const hour = now.getUTCHours();
  
  // Market closed: Friday 22:00 UTC - Sunday 22:00 UTC
  if (day === 6) return true; // Saturday
  if (day === 0 && hour < 22) return true; // Sunday before 22:00
  if (day === 5 && hour >= 22) return true; // Friday after 22:00
  
  return false;
};

const MarketSessionBanner = ({ compact = false }) => {
  const [sessions, setSessions] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [marketClosed, setMarketClosed] = useState(false);

  useEffect(() => {
    const updateSessions = () => {
      setSessions(getActiveSessions());
      setCurrentTime(new Date());
      setMarketClosed(isMarketClosed());
    };
    
    updateSessions();
    const interval = setInterval(updateSessions, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, []);

  if (marketClosed) {
    return (
      <div style={{
        background: 'linear-gradient(90deg, #7f1d1d 0%, #991b1b 100%)',
        padding: compact ? '8px 12px' : '10px 16px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        marginBottom: compact ? '8px' : '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={16} style={{ color: '#FCA5A5' }} />
          <span style={{ fontSize: '12px', color: '#FCA5A5', fontWeight: '600' }}>
            MARKET DITUTUP
          </span>
        </div>
        <span style={{ fontSize: '11px', color: '#FDA4AF' }}>
          Dibuka Ahad 10:00 PM (MYT)
        </span>
      </div>
    );
  }

  if (sessions.length === 0) {
    return null;
  }

  if (compact) {
    const primarySession = sessions[0];
    return (
      <div style={{
        background: `${primarySession.color}20`,
        border: `1px solid ${primarySession.color}40`,
        padding: '6px 10px',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '11px'
      }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: primarySession.color,
          animation: 'pulse 2s infinite'
        }} />
        <span style={{ color: primarySession.color, fontWeight: '600' }}>
          {primarySession.name}
        </span>
        <span style={{ color: 'var(--text-muted)' }}>
          {primarySession.hoursRemaining}h left
        </span>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border-subtle)',
      borderRadius: '12px',
      padding: '12px 16px',
      marginBottom: '16px'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '10px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Globe size={16} style={{ color: 'var(--accent-pink)' }} />
          <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>
            MARKET SESSION
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Clock size={12} style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {currentTime.toLocaleTimeString('ms-MY', { hour: '2-digit', minute: '2-digit' })} MYT
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {sessions.map((session) => (
          <div 
            key={session.name}
            style={{
              background: `${session.color}15`,
              border: `1px solid ${session.color}30`,
              borderRadius: '8px',
              padding: '8px 12px',
              flex: '1',
              minWidth: '120px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: session.color,
                animation: 'pulse 2s infinite'
              }} />
              <span style={{ 
                fontSize: '12px', 
                fontWeight: '700', 
                color: session.color 
              }}>
                {session.name}
              </span>
              <span style={{ 
                fontSize: '10px', 
                color: 'var(--text-muted)',
                marginLeft: 'auto'
              }}>
                {session.hoursRemaining}h left
              </span>
            </div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {session.pairs.slice(0, 3).map((pair) => (
                <span 
                  key={pair}
                  style={{
                    fontSize: '9px',
                    padding: '2px 6px',
                    background: `${session.color}20`,
                    color: session.color,
                    borderRadius: '4px'
                  }}
                >
                  {pair}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {sessions.length > 1 && (
        <div style={{
          marginTop: '10px',
          padding: '8px 10px',
          background: 'rgba(74, 222, 128, 0.1)',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <TrendingUp size={14} style={{ color: '#4ADE80' }} />
          <span style={{ fontSize: '11px', color: '#4ADE80' }}>
            🔥 <strong>Overlap Session!</strong> Volatiliti tinggi - peluang trading terbaik
          </span>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default MarketSessionBanner;
